import {createState, Store} from "@ngneat/elf";
import {
  deleteEntities,
  selectAllEntities,
  selectAllEntitiesApply,
  selectEntity,
  setEntities,
  upsertEntities,
  withEntities
} from "@ngneat/elf-entities";
import {Event, Place} from "@app/interfaces";
import {Injectable} from "@angular/core";
import {localStorageStrategy, persistState} from "@ngneat/elf-persist-state";
import {map} from "rxjs";

const MAX_PERSISTED_EVENTS = 50;
const EVENTS_STORAGE_KEY = 'events-v2';
const LEGACY_EVENTS_STORAGE_KEY = 'events';

export interface EventsStoreState {
  ids: string[];
  entities: Record<string, Event>;
}

export function selectPersistedEvents(state: EventsStoreState): EventsStoreState {
  const startOfToday: Date = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  const events: Event[] = state.ids
    .map((id: string) => state.entities[id])
    .filter((event: Event | undefined): event is Event => event !== undefined)
    .filter((event: Event) => new Date(event.event_date_start).getTime() >= startOfToday.getTime())
    .sort((left: Event, right: Event) => new Date(left.event_date_start).getTime() - new Date(right.event_date_start).getTime())
    .slice(0, MAX_PERSISTED_EVENTS);
  const ids: string[] = events.map((event: Event) => event.id);
  const entities: Record<string, Event> = Object.fromEntries(
    ids.map((id: string) => [id, state.entities[id]])
  );

  return {...state, ids, entities};
}

const clearLegacyEventsCache = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.removeItem(LEGACY_EVENTS_STORAGE_KEY);
  } catch (error) {
    console.warn('EventsRepository.clearLegacyEventsCache', error);
  }
};

clearLegacyEventsCache();

const {state, config} = createState(withEntities<Event>());

const eventsStore = new Store({name: 'events', state, config});

const persist = persistState(eventsStore, {
  key: EVENTS_STORAGE_KEY,
  storage: localStorageStrategy,
  source: (store) => store.pipe(
    map(selectPersistedEvents)
  ),
  preStoreInit: selectPersistedEvents,
});

@Injectable({providedIn: 'root'})
export class EventsRepository {
  events$ = eventsStore.pipe(selectAllEntities());

  updateEvents(events: Event[]) {
    console.log(`${this.constructor.name}.updateEvents`, events.length);
    eventsStore.update(setEntities(events));
  }

  filterById(id: string) {
    return eventsStore.pipe(selectEntity(id));
  }

  filterByPlace(place: Place) {
    console.log(`${this.constructor.name}.filterByPlace`, place);
    return eventsStore.pipe(
      selectAllEntitiesApply({
        filterEntity: (e) => e.owner.id === place.id
      })
    );
  }

  constructor() {
    console.log(`${this.constructor.name}.constructor`);

    this.events$.subscribe(events => {

    });
  }

  addEvent(event: Event) {
    console.log(`${this.constructor.name}.addEvent`, event);

    try {
      eventsStore.update(upsertEntities(event));
    } catch (error) {
      console.error(`${this.constructor.name}.addEvent: error`, error);
    }
  }

  remove(event: Event) {
    console.log(`${this.constructor.name}.remove`, event);
    eventsStore.update(deleteEntities(event.id));
  }
}
